/**
 * Single source of truth: app path → RBAC permission module(s).
 *
 * - `null`  → open to any authenticated tenant user (self-service / always-on surfaces)
 * - string[] → user needs ANY of these modules (same OR semantics as `canSee`)
 *
 * Must stay aligned with AppShell MODULE_RAIL / EXTRA_GUARD_PATHS and with
 * backend `requirePermission` / `requireAnyPermission` modules. SaaS package
 * keys (ats, helpdesk, procurement, bi, travel, …) are NOT RBAC modules.
 */

export type RouteModuleGuard = { prefix: string; modules: string[] | null };

export const ROUTE_MODULE_GUARDS: RouteModuleGuard[] = [
  // Longer prefixes first is handled by modulesForPath sort — order here is for readability.
  { prefix: "/dashboard", modules: null },
  { prefix: "/employee", modules: null },
  { prefix: "/my-portal", modules: null },
  { prefix: "/account-settings", modules: null },
  { prefix: "/settings", modules: null },
  { prefix: "/profile", modules: null },
  { prefix: "/operations/account-settings", modules: null },
  { prefix: "/operations/settings", modules: null },
  { prefix: "/approvals", modules: null },
  { prefix: "/helpdesk", modules: null },

  { prefix: "/organization", modules: ["organization"] },
  { prefix: "/users", modules: ["users", "roles"] },
  { prefix: "/audit-log", modules: ["audit_log"] },

  { prefix: "/hr", modules: ["hr"] },
  { prefix: "/recruitment", modules: ["hr"] },
  { prefix: "/performance", modules: null },
  { prefix: "/ld", modules: ["hr"] },

  { prefix: "/finance", modules: ["finance"] },
  { prefix: "/finance/payroll", modules: ["payroll", "finance", "hr"] },
  { prefix: "/operations/finance/payroll", modules: ["payroll", "finance", "hr"] },
  { prefix: "/operations/payroll", modules: ["payroll", "finance", "hr"] },
  { prefix: "/tally", modules: ["finance"] },
  { prefix: "/procurement", modules: ["finance"] },
  { prefix: "/integrations", modules: ["finance"] },
  { prefix: "/travel", modules: ["finance", "hr"] },

  { prefix: "/payroll", modules: ["payroll", "finance", "hr"] },
  { prefix: "/catalog", modules: ["catalog"] },
  { prefix: "/inventory", modules: ["inventory"] },
  { prefix: "/crm", modules: ["crm"] },
  { prefix: "/operations", modules: ["operations"] }, // tab=tickets assignee self-service: App.tsx guard
  { prefix: "/service", modules: ["operations"] },
  { prefix: "/admin", modules: ["admin"] },
  { prefix: "/it", modules: ["admin"] },
  { prefix: "/quality", modules: ["quality"] },
  { prefix: "/manufacturing", modules: ["manufacturing"] },
  { prefix: "/dms", modules: ["dms"] },
  { prefix: "/documents", modules: ["dms"] },
  { prefix: "/operations/documents", modules: ["dms", "operations"] },
  { prefix: "/reports", modules: ["reports"] },
  { prefix: "/dev-tools", modules: ["dev"] },
];

/** Resolve RBAC module(s) required for a given app path. */
export function modulesForPath(path: string): string[] | null {
  const cleanPath = path.split("?")[0].split("#")[0];
  const searchPart = path.includes("?")
    ? path.split("?")[1]
    : typeof window !== "undefined"
      ? window.location.search.replace(/^\?/, "")
      : "";
  const params = new URLSearchParams(searchPart);
  const tab = params.get("tab");

  // Self-service personal tabs on payroll are accessible to all authenticated tenant users.
  // PayrollPage natively defaults to "my-payslips" for standard employees and "runs" for managers.
  if (
    cleanPath.includes("payroll") &&
    (!tab || tab.startsWith("my-") || cleanPath.endsWith("/salary-slips") || cleanPath.includes("/salary-slips/") || cleanPath.endsWith("/my-payslips"))
  ) {
    return null;
  }

  const candidates = ROUTE_MODULE_GUARDS.filter(
    (c) => cleanPath === c.prefix || cleanPath.startsWith(c.prefix + "/"),
  );
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.prefix.length - a.prefix.length);
  return candidates[0].modules;
}
